import React, { createContext, useContext, useReducer, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useFacility } from './FacilityContext';
import { initialState, bookingReducer } from '../hooks/booking/bookingReducer';
import { useAPIMode } from '../hooks/booking/bookingUtils';
import { useBookingDataLoader } from '../hooks/booking/useBookingDataLoader';
import { useBookingActions as useBookingActionsInternal } from '../hooks/booking/useBookingActions';
import { useSlotAvailability as useSlotAvailabilityInternal } from '../hooks/booking/useSlotAvailability';
import { usePayments as usePaymentsInternal } from '../hooks/booking/usePayments';
import { useFacilityData as useFacilityDataInternal } from '../hooks/booking/useFacilityData';
import { useSlotConfiguration as useSlotConfigurationInternal } from '../hooks/booking/useSlotConfiguration';
import { useBookingPersistence } from '../hooks/booking/useBookingPersistence';

export const BookingContext = createContext();

export const BookingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { activeFacilityId, activeFacility } = useFacility();
  const useAPI = useAPIMode();

  const { loading, reloadBookings } = useBookingDataLoader({
    dispatch,
    useAPI,
    isAuthenticated,
    authLoading,
    activeFacilityId,
  });

  useBookingPersistence({ state, activeFacilityId });

  const {
    slotConfiguration,
    updateSlotConfiguration,
    addServiceConfiguration,
    updateServiceConfiguration,
    removeServiceConfiguration,
    clearServiceConfig,
  } = useSlotConfigurationInternal({ dispatch, state, activeFacilityId });

  const {
    getGroundById,
    getServiceById,
    getAllServices,
    getSeasonById,
    getSlotDetails,
    calculateSlotPrice,
    getSlotAvailability,
  } = useSlotAvailabilityInternal({ state });

  const { addBooking, cancelBooking, archiveBooking, updateBookingStatus } = useBookingActionsInternal({
    dispatch,
    useAPI,
    activeFacility,
    getGroundById,
    getServiceById,
    getAllServices,
    getSeasonById,
    getSlotDetails,
    calculateSlotPrice,
    getSlotAvailability,
  });

  const { markPaymentComplete, fetchPaymentHistory, paymentHistory, historyLoading } = usePaymentsInternal({
    dispatch,
    useAPI,
  });

  const { facility, grounds, updateFacility, clearAllData } = useFacilityDataInternal({
    dispatch,
    useAPI,
    state,
  });

  const value = useMemo(
    () => ({
      ...state,
      loading,
      reloadBookings,
      addBooking,
      cancelBooking,
      archiveBooking,
      updateBookingStatus,
      markPaymentComplete,
      fetchPaymentHistory,
      paymentHistory,
      historyLoading,
      updateFacility,
      clearAllData,
      facility,
      grounds,
      slotConfiguration,
      updateSlotConfiguration,
      addServiceConfiguration,
      updateServiceConfiguration,
      removeServiceConfiguration,
      clearServiceConfig,
      getGroundById,
      getServiceById,
      getAllServices,
      getSeasonById,
      getSlotDetails,
      calculateSlotPrice,
      getSlotAvailability,
    }),
    [
      state,
      loading,
      reloadBookings,
      addBooking,
      cancelBooking,
      archiveBooking,
      updateBookingStatus,
      markPaymentComplete,
      fetchPaymentHistory,
      paymentHistory,
      historyLoading,
      updateFacility,
      clearAllData,
      facility,
      grounds,
      slotConfiguration,
      updateSlotConfiguration,
      addServiceConfiguration,
      updateServiceConfiguration,
      removeServiceConfiguration,
      clearServiceConfig,
      getGroundById,
      getServiceById,
      getAllServices,
      getSeasonById,
      getSlotDetails,
      calculateSlotPrice,
      getSlotAvailability,
    ]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};
